import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createSystemStripeCheckoutSession,
  downloadBillingInvoice,
  downloadBillingInvoicePdf,
  generateBillingInvoicePdf,
  getBillingInvoice,
  getBillingInvoiceAccountantEmail,
  markBillingInvoicePaid,
  markInvoicePaidManual,
  voidBillingInvoice,
} from '../api/billingPortalApi';
import { getStripeStatus } from '../api/stripeApi';
import {
  getInvoiceDisplayNumber,
  isExternalAccountingInvoice,
} from '../billing/externalInvoiceDisplay';
import {
  formatInvoiceDueOverview,
  resolveInvoiceDueOverview,
} from '../billing/invoiceDueDisplay';
import { stripeOnlinePaymentControlsFromStripeStatus } from '../billing/stripeOnlinePaymentUi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { DetailField } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/Button';

const { semantic } = colorTokens;

const MANUAL_METHODS = ['manual_invoice', 'manual_bank_transfer', 'manual_cash'];

const STRIPE_QR_HIDDEN_STATUSES = [
  'hidden_stripe_checkout',
  'hidden_paid_stripe',
  'hidden_stripe',
  'hiddenPaidStripe',
  'hiddenStripeCheckout',
];

const UNAVAILABLE_QR_STATUSES = [
  'unavailable',
  'unsupported_payment_method',
  'missing_bank_details',
  'unsupportedPaymentMethod',
  'missingBankDetails',
  'hidden_paid_invoice',
];

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
  gap: 2.5,
  minWidth: 0,
  maxWidth: '100%',
  '& > *': {
    minWidth: 0,
    maxWidth: '100%',
  },
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function getQrStatusCompact(status, t, emptyValue) {
  if (!status) {
    return { label: emptyValue, tone: 'Neutral', badgeStatus: 'empty' };
  }
  if (status === 'generated' || status === 'available') {
    return {
      label: t('billing:invoices.qr.badgeAvailable'),
      tone: 'Success',
      badgeStatus: 'available',
    };
  }
  if (STRIPE_QR_HIDDEN_STATUSES.includes(status)) {
    return {
      label: t('billing:invoices.qr.badgeStripeNoQr'),
      tone: 'Neutral',
      badgeStatus: 'stripe_no_qr',
    };
  }
  if (UNAVAILABLE_QR_STATUSES.includes(status)) {
    return {
      label: t('billing:invoices.qr.badgeUnavailable'),
      tone: 'Warning',
      badgeStatus: 'unavailable',
    };
  }
  if (String(status).includes('stripe')) {
    return {
      label: t('billing:invoices.qr.badgeStripeNoQr'),
      tone: 'Neutral',
      badgeStatus: 'stripe_no_qr',
    };
  }
  if (String(status).startsWith('hidden')) {
    return {
      label: t('billing:invoices.qr.badgeHidden'),
      tone: 'Neutral',
      badgeStatus: 'hidden',
    };
  }
  return {
    label: t('billing:invoices.qr.badgeUnavailable'),
    tone: 'Warning',
    badgeStatus: 'unavailable',
  };
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

function defaultPaidUntil(fromDate) {
  const date = new Date(fromDate);
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().slice(0, 10);
}

export function BillingInvoiceDetailPage() {
  const { t } = useTranslation(['billing', 'common', 'stripe']);
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    payment_method: 'manual_bank_transfer',
    paid_from: new Date().toISOString().slice(0, 10),
    paid_until: defaultPaidUntil(new Date()),
    paid_at: '',
    amount: '',
    reference: '',
    note: '',
  });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfDetails, setPdfDetails] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [accountantBusy, setAccountantBusy] = useState(false);
  const [accountantText, setAccountantText] = useState('');

  const emptyValue = t('common:empty_value');
  const stripeUi = stripeOnlinePaymentControlsFromStripeStatus(stripeStatus);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getBillingInvoice(id),
      getStripeStatus().catch(() => null),
    ])
      .then(([invoiceData, statusData]) => {
        setInvoice(invoiceData);
        setStripeStatus(statusData);
      })
      .catch(() => setError(t('billing:load_invoice_failed')))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkPaid = async () => {
    try {
      await markBillingInvoicePaid(id);
      setMessage(t('billing:mark_paid_success'));
      load();
    } catch {
      setError(t('billing:mark_paid_failed'));
    }
  };

  const handleManualMarkPaid = async () => {
    setManualSaving(true);
    setError('');
    try {
      await markInvoicePaidManual(id, {
        ...manualForm,
        amount: manualForm.amount ? Number(manualForm.amount) : undefined,
        paid_at: manualForm.paid_at || undefined,
      });
      setManualOpen(false);
      setMessage(t('billing:mark_paid_manual_success'));
      load();
    } catch {
      setError(t('billing:mark_paid_manual_failed'));
    } finally {
      setManualSaving(false);
    }
  };

  const handleStripeCheckout = async () => {
    setError('');
    try {
      const session = await createSystemStripeCheckoutSession(id);
      if (session.status === 'disabled') {
        setMessage(t('stripe:errors.checkoutDisabled'));
        return;
      }
      const url = session.checkout_url || session.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        setMessage(t('stripe:messages.checkoutSessionCreated'));
        return;
      }
      setMessage(session.message || t('billing:stripe_checkout_failed'));
    } catch {
      setError(t('billing:stripe_checkout_failed'));
    }
  };

  const handleVoid = async () => {
    try {
      await voidBillingInvoice(id, { reason: t('billing:void_reason') });
      setMessage(t('billing:void_success'));
      load();
    } catch {
      setError(t('billing:void_failed'));
    }
  };

  const handleDownload = async () => {
    try {
      if (invoice?.pdf_status === 'generated') {
        const blob = await downloadBillingInvoicePdf(id);
        triggerBlobDownload(blob, `${invoice.public_number || `invoice-${id}`}.pdf`);
        return;
      }
      const info = await downloadBillingInvoice(id);
      if (info.status === 'external_url' && info.url) {
        window.open(info.url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (info.status === 'generated_pdf') {
        const blob = await downloadBillingInvoicePdf(id);
        triggerBlobDownload(blob, `${invoice?.public_number || `invoice-${id}`}.pdf`);
        return;
      }
      setMessage(info.message || t('billing:invoices.pdf.notAvailable'));
    } catch {
      setError(t('billing:download_failed'));
    }
  };

  const handleCopyAccountantEmail = async () => {
    setAccountantBusy(true);
    setError('');
    setMessage('');
    try {
      const payload = await getBillingInvoiceAccountantEmail(id);
      const text = payload?.text || '';
      if (!text) {
        setError(t('billing:accountantEmail.empty'));
        return;
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
      setAccountantText(text);
      setMessage(t('billing:accountantEmail.copied'));
    } catch (err) {
      const missing = err?.response?.data?.missing_fields;
      setError(
        missing?.length
          ? t('billing:accountantEmail.snapshotIncomplete', { fields: missing.join(', ') })
          : t('billing:accountantEmail.failed'),
      );
    } finally {
      setAccountantBusy(false);
    }
  };

  const handleGeneratePdf = async () => {
    setPdfBusy(true);
    setError('');
    setPdfDetails(null);
    try {
      const result = await generateBillingInvoicePdf(id);
      if (result.status === 'generated') {
        setMessage(t('billing:invoices.pdf.generated'));
        setPdfDetails(result.details || null);
        if (result.invoice) {
          setInvoice(result.invoice);
        } else {
          load();
        }
        return;
      }
      setError(result.details?.error_message || t('billing:invoices.pdf.failed'));
      setPdfDetails(result.details || null);
      if (result.invoice) {
        setInvoice(result.invoice);
      } else {
        load();
      }
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.details?.error_message || t('billing:invoices.pdf.failed'));
      setPdfDetails(payload?.details || null);
      if (payload?.invoice) {
        setInvoice(payload.invoice);
      }
    } finally {
      setPdfBusy(false);
    }
  };

  const lineColumns = useMemo(
    () => [
      {
        id: 'description',
        label: t('billing:description'),
        render: (line) => line.description || emptyValue,
      },
      {
        id: 'quantity',
        label: t('billing:qty'),
        render: (line) => (
          line.quantity === 0 || line.quantity ? line.quantity : emptyValue
        ),
      },
      {
        id: 'unit_amount',
        label: t('billing:unit'),
        render: (line) => (
          line.unit_amount === 0 || line.unit_amount ? line.unit_amount : emptyValue
        ),
      },
      {
        id: 'amount',
        label: t('billing:amount'),
        render: (line) => (
          line.amount === 0 || line.amount ? line.amount : emptyValue
        ),
      },
    ],
    [emptyValue, t],
  );

  const eventColumns = useMemo(
    () => [
      {
        id: 'created_at',
        label: t('common:created'),
        render: (event) => event.created_at || emptyValue,
      },
      {
        id: 'event_type',
        label: t('common:type'),
        render: (event) => event.event_type || emptyValue,
      },
      {
        id: 'message',
        label: t('billing:note'),
        render: (event) => event.message || emptyValue,
      },
    ],
    [emptyValue, t],
  );

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (!invoice) {
    return (
      <SectionCard variant="Danger">
        <Alert severity="error" sx={{ bgcolor: 'transparent', p: 0 }}>
          {error || t('billing:invoice_not_found')}
        </Alert>
      </SectionCard>
    );
  }

  const isUnpaid = invoice.status !== 'paid' && invoice.status !== 'void';
  const isExternalAccounting = isExternalAccountingInvoice(invoice);
  const pdfGenerated = invoice.pdf_status === 'generated';
  const pdfFailed = invoice.pdf_status === 'failed';
  const pdfGenerateLabel = pdfGenerated || pdfFailed
    ? t('billing:invoices.pdf.regenerate')
    : t('billing:invoices.pdf.generate');
  const pdfStatusLabel = isExternalAccounting
    ? t('billing:externalAccounting.pdfNotApplicable')
    : (invoice.display_status_label
      || (invoice.display_status
        ? t(`billing:invoices.status.${invoice.display_status}`, { defaultValue: invoice.display_status })
        : null)
      || invoice.pdf_status
      || t('billing:invoices.pdf.notAvailable'));
  const qrCompact = getQrStatusCompact(invoice.payment_qr_status, t, emptyValue);
  const variableSymbol = invoice.variable_symbol || invoice.bank_transfer_details?.variable_symbol;
  const storageDisk = invoice.pdf_disk || pdfDetails?.disk;
  const hasTechnicalFields = Boolean(
    invoice.stripe_checkout_session_id
    || storageDisk
    || invoice.pdf_generated_at
    || invoice.pdf_last_attempt_at
    || invoice.pdf_path,
  );
  const displayNumber = getInvoiceDisplayNumber(
    invoice,
    t('billing:invoice_title', { id: invoice.id }),
  );
  const dueOverview = resolveInvoiceDueOverview(invoice);
  const dueOverviewLabel = formatInvoiceDueOverview(dueOverview, t, emptyValue);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="Detail"
        title={displayNumber}
        subtitle={`${invoice.customer?.name || invoice.customer_id} · ${
          isExternalAccounting
            ? t('billing:externalAccounting.sourceExternal')
            : invoice.provider
        }`}
        backTo="/billing/invoices"
        badge={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <StatusBadge
              status={invoice.display_status || invoice.status}
              category="invoice"
              size="Md"
            />
            {invoice.provider && (
              <StatusBadge
                status={invoice.provider}
                category="provider"
                size="Md"
              />
            )}
          </Stack>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {accountantText && (
        <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-wrap' }} data-testid="accountant-email-preview">
          {accountantText}
        </Alert>
      )}

      <SectionCard variant="Default" title={t('billing:payment_status_title')} sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
          <StatusBadge
            status={invoice.display_status || invoice.status}
            category="invoice"
            size="Sm"
          />
          <Typography
            sx={{
              fontFamily: typographyTokens.fontFamily,
              fontWeight: typographyTokens.fontWeightSemibold,
              fontSize: 13,
              color: semantic.text.primary,
            }}
          >
            {t('billing:total')}: {invoice.total_amount} {invoice.currency}
          </Typography>
          <Typography
            sx={{
              fontFamily: typographyTokens.fontFamily,
              fontSize: 13,
              color: semantic.text.secondary,
            }}
          >
            {t('billing:due')}: {invoice.amount_due}
          </Typography>
        </Stack>

        <Box sx={{ ...detailGridSx, mb: 2.5 }}>
          <DetailField label={t('billing:payment_method')} value={invoice.payment_method || emptyValue} />
          <DetailField label={t('billing:payment_flow')} value={invoice.payment_flow || emptyValue} />
          <DetailField
            label={t('billing:due')}
            value={invoice.due_at ? new Date(invoice.due_at).toLocaleDateString() : emptyValue}
          />
          <DetailField label={t('billing:invoices.due.overview')} value={dueOverviewLabel} />
          {variableSymbol && (
            <DetailField label={t('billing:invoices.qr.variableSymbol')} value={variableSymbol} />
          )}
          {invoice.payment_qr_status && (
            <DetailField label={t('billing:invoices.qr.status')}>
              <StatusBadge
                status={qrCompact.badgeStatus}
                category="generic"
                apiLabel={qrCompact.label}
                size="Sm"
                tone={qrCompact.tone}
              />
            </DetailField>
          )}
          <DetailField label={t('billing:paid_from')} value={invoice.paid_from || emptyValue} />
          <DetailField label={t('billing:paid_until')} value={invoice.paid_until || emptyValue} />
          <DetailField
            label={t('billing:coverage_period')}
            value={`${invoice.coverage_period_start || '—'} → ${invoice.coverage_period_end || '—'}`}
          />
          <DetailField label={t('billing:grace_until')} value={invoice.grace_until || emptyValue} />
          {invoice.subscription?.paid_until && (
            <DetailField
              label={t('billing:subscription_paid_until')}
              value={invoice.subscription.paid_until}
            />
          )}
          <DetailField label={t('billing:invoices.pdf.status')}>
            <StatusBadge
              status={invoice.pdf_status || invoice.display_status || 'pending'}
              category="generic"
              apiLabel={pdfStatusLabel}
              size="Sm"
              tone={pdfGenerated ? 'Success' : pdfFailed ? 'Danger' : 'Neutral'}
            />
          </DetailField>
          {(invoice.payment_method || invoice.stripe_payment_status) && (
            <DetailField label={t('billing:invoices.payment.status')}>
              <StatusBadge
                status={invoice.stripe_payment_status || invoice.payment_method || 'unknown'}
                category="generic"
                apiLabel={invoice.stripe_payment_status || invoice.payment_method || emptyValue}
                size="Sm"
              />
            </DetailField>
          )}
        </Box>

        {invoice.bank_transfer_details_available && invoice.bank_transfer_details && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                mb: 1.5,
                fontSize: 12,
                letterSpacing: '0.24px',
                color: semantic.text.muted,
              }}
            >
              {t('billing:invoices.qr.bankDetails')}
            </Typography>
            <Box sx={detailGridSx}>
              <DetailField
                label="IBAN"
                value={invoice.bank_transfer_details.iban || emptyValue}
              />
              {invoice.bank_transfer_details.bic && (
                <DetailField label="BIC" value={invoice.bank_transfer_details.bic} />
              )}
              {invoice.bank_transfer_details.variable_symbol && (
                <DetailField
                  label={t('billing:invoices.qr.variableSymbol')}
                  value={invoice.bank_transfer_details.variable_symbol}
                />
              )}
              {invoice.bank_transfer_details.amount && (
                <DetailField
                  label={t('billing:amount')}
                  value={`${invoice.bank_transfer_details.amount} ${invoice.bank_transfer_details.currency || invoice.currency}`}
                />
              )}
              {invoice.bank_transfer_details.due_date && (
                <DetailField
                  label={t('billing:due')}
                  value={invoice.bank_transfer_details.due_date}
                />
              )}
            </Box>
          </Box>
        )}

        <Stack spacing={1.5} sx={{ mb: 2.5 }}>
          {invoice.payment_qr_message_key && (
            <Alert severity="info">
              {t(invoice.payment_qr_message_key, {
                defaultValue: t(`billing:invoices.qr.${invoice.payment_qr_status}`, {
                  defaultValue: invoice.payment_qr_status,
                }),
              })}
            </Alert>
          )}
          {storageDisk && storageDisk !== 's3' && (
            <Alert severity="warning">{t('billing:invoices.pdf.notStoredInS3')}</Alert>
          )}
          {invoice.status === 'paid' && !pdfGenerated && !pdfFailed && (
            <Alert severity="info">{t('billing:invoices.pdf.notAvailable')}</Alert>
          )}
          {invoice.status === 'paid' && pdfFailed && (
            <Alert severity="warning">{t('billing:invoices.pdf.autoGenerationFailed')}</Alert>
          )}
          {pdfGenerated && (
            <Alert severity="success">{t('billing:invoices.pdf.downloadAvailable')}</Alert>
          )}
          {(pdfDetails?.warnings?.includes('tax_mode_missing') || pdfDetails?.warnings?.includes('taxModeMissing')) && (
            <Alert severity="warning">{t('billing:invoices.warnings.taxModeMissing')}</Alert>
          )}
          {(pdfDetails?.warnings?.includes('status_reconciled') || pdfDetails?.status_reconciled) && (
            <Alert severity="info">{t('billing:invoices.warnings.statusReconciled')}</Alert>
          )}
          {pdfDetails?.warnings?.includes('missing_issuer_fields') && (
            <Alert severity="warning">{t('billing:invoices.pdf.issuerMissingFields')}</Alert>
          )}
          {pdfDetails?.warnings?.includes('missing_customer_billing_fields') && (
            <Alert severity="warning">{t('billing:invoices.pdf.customerMissingFields')}</Alert>
          )}
          {pdfDetails?.logo_status === 'text_fallback' && (
            <Alert severity="info">{t('billing:invoices.pdf.logoFallback')}</Alert>
          )}
          {pdfFailed && invoice.pdf_error_message && (
            <Alert severity="warning">
              {t('billing:invoices.pdf.safeError')}: {invoice.pdf_error_message}
            </Alert>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{
            pt: 2,
            borderTop: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {pdfGenerated && (
            <Button variant="Primary" onClick={handleDownload} disabled={pdfBusy}>
              {t('billing:invoices.pdf.download')}
            </Button>
          )}
          {!isExternalAccounting && (
            <Button
              variant="Secondary"
              onClick={handleCopyAccountantEmail}
              disabled={accountantBusy}
              loading={accountantBusy}
              data-testid="copy-accountant-email"
            >
              {t('billing:accountantEmail.copy')}
            </Button>
          )}
          {isUnpaid && (
            <Button
              variant="Secondary"
              onClick={() => {
                setManualForm((prev) => ({
                  ...prev,
                  payment_method: 'manual_bank_transfer',
                  paid_from: invoice.coverage_period_start || invoice.period_start || prev.paid_from,
                  paid_until: invoice.coverage_period_end || invoice.period_end || prev.paid_until,
                  amount: invoice.total_amount != null ? String(invoice.total_amount) : prev.amount,
                  reference: invoice.external_invoice_number || invoice.display_number || prev.reference,
                }));
                setManualOpen(true);
              }}
            >
              {t('billing:mark_paid_manual')}
            </Button>
          )}
          {isUnpaid && stripeUi.showCreateCheckoutCta && (
            <Button variant="Ghost" onClick={handleStripeCheckout}>
              {t('stripe:actions.createCheckout')}
            </Button>
          )}
          {isUnpaid && (
            <Button variant="Ghost" onClick={handleMarkPaid}>
              {t('billing:mark_paid')}
            </Button>
          )}
          {!isExternalAccounting && (
            <Button variant="Ghost" onClick={handleGeneratePdf} disabled={pdfBusy} loading={pdfBusy}>
              {pdfFailed ? t('billing:invoices.pdf.retry') : pdfGenerateLabel}
            </Button>
          )}
          {!pdfGenerated && !isExternalAccounting && (
            <Button variant="Ghost" onClick={handleDownload}>
              {t('billing:download_link')}
            </Button>
          )}
          {isUnpaid && (
            <Button variant="Danger" onClick={handleVoid} sx={{ ml: { sm: 'auto' } }}>
              {t('billing:void')}
            </Button>
          )}
        </Stack>
      </SectionCard>

      {hasTechnicalFields && (
        <SectionCard variant="Default" title={t('billing:technical_details')} sx={{ mb: 2.5 }}>
          <Box sx={detailGridSx}>
            {invoice.stripe_checkout_session_id && (
              <DetailField label={t('billing:invoices.payment.stripeReference')}>
                <Typography sx={monoSx}>{invoice.stripe_checkout_session_id}</Typography>
              </DetailField>
            )}
            {storageDisk && (
              <DetailField label={t('billing:invoices.pdf.storageDisk')} value={storageDisk} />
            )}
            {invoice.pdf_generated_at && (
              <DetailField
                label={t('billing:invoices.pdf.generatedAt')}
                value={new Date(invoice.pdf_generated_at).toLocaleString()}
              />
            )}
            {invoice.pdf_last_attempt_at && (
              <DetailField
                label={t('billing:invoices.pdf.lastAttemptAt')}
                value={new Date(invoice.pdf_last_attempt_at).toLocaleString()}
              />
            )}
            {invoice.pdf_path && (
              <DetailField label={t('billing:invoices.pdf.storagePath')}>
                <Typography sx={monoSx}>{invoice.pdf_path}</Typography>
              </DetailField>
            )}
          </Box>
        </SectionCard>
      )}

      <SectionCard variant="Default" title={t('billing:lines')} noPadding sx={{ mb: 2.5 }}>
        <DataTable
          type="Default"
          embedded
          columns={lineColumns}
          rows={invoice.lines || []}
          getRowKey={(line) => line.id}
          emptyMessage={t('common:no_data')}
        />
      </SectionCard>

      <SectionCard variant="Default" title={t('billing:events')} noPadding sx={{ mb: 0 }}>
        <DataTable
          type="Default"
          embedded
          columns={eventColumns}
          rows={invoice.events || []}
          getRowKey={(event) => event.id}
          emptyMessage={t('common:no_data')}
        />
      </SectionCard>

      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing:mark_paid_manual')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label={t('billing:payment_method')}
              value={manualForm.payment_method}
              onChange={(e) => setManualForm({ ...manualForm, payment_method: e.target.value })}
            >
              {MANUAL_METHODS.map((method) => (
                <MenuItem key={method} value={method}>{t(`billing:payment_methods.${method}`)}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label={t('billing:paid_from')}
              value={manualForm.paid_from}
              onChange={(e) => setManualForm({
                ...manualForm,
                paid_from: e.target.value,
                paid_until: defaultPaidUntil(e.target.value),
              })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label={t('billing:paid_until')}
              value={manualForm.paid_until}
              onChange={(e) => setManualForm({ ...manualForm, paid_until: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('billing:reference')}
              value={manualForm.reference}
              onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })}
            />
            <TextField
              label={t('billing:note')}
              multiline
              rows={2}
              value={manualForm.note}
              onChange={(e) => setManualForm({ ...manualForm, note: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="Ghost" onClick={() => setManualOpen(false)}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="Primary"
            onClick={handleManualMarkPaid}
            loading={manualSaving}
            disabled={manualSaving}
          >
            {t('billing:mark_paid_manual')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
